import { Dialog, DialogContent, DialogHeader, DialogTitle } from "design-system/atoms/Dialog"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import { Loading } from "modules/shared/components/Loading"
import { useTranslation } from "react-i18next"
import { BackofficeGetClusterResponseDataSchema } from "schema"
import z from "zod"

type ClusterParams = {
  companyId?: string
  domain?: string
  goalId?: string
  campaignId?: string | null
  from?: string
  to?: string
  biasFilter?: string | null
  score?: number
  epsilon?: string | null
  samplingFactor?: string | null
  selectionMethod?: string | null
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  cluster: z.infer<typeof BackofficeGetClusterResponseDataSchema>
  params: ClusterParams
  isPending: boolean
}

export const ClusterDetailDialog = ({ open, onOpenChange, cluster, params, isPending }: Props) => {
  const { t } = useTranslation()
  const paramEntries = Object.entries(params ?? {}).filter(([, value]) => value !== undefined)
  const formatLabel = (label: string) =>
    label
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (char) => char.toUpperCase())
      .trim()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:!max-w-3xl max-h-[95vh] w-full h-full flex flex-col p-0 gap-0">
        <DialogHeader className="px-4 pt-4 pb-3 border-b">
          <DialogTitle>{t("backoffice.clusters.detail.title")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col flex-1 overflow-auto gap-8 p-6">
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 w-full">
            {paramEntries.map(([label, value]) => (
              <div key={label} className="flex flex-col gap-1">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  {formatLabel(label)}
                </dt>
                <dd className="font-mono text-sm break-all">{String(value)}</dd>
              </div>
            ))}
          </dl>
          {isPending ? (
            <Loading />
          ) : (
            <MarkdownRenderer expandLevel={2}>
              {`\`\`\`json\n${JSON.stringify(cluster, null, 2)}\n\`\`\``}
            </MarkdownRenderer>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
